/**
 * View one node in the artifact tree, which may be a folder or a file.
 */

import {Icon} from '@wandb/weave/components/Icon';
import numeral from 'numeral';
import React from 'react';
import {CellInfo} from 'react-table';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {Table} from '../Table';
import {ArtifactFileViewer} from './ArtifactFileViewer';

type ArtifactNodeViewerProps = {
  entityName: string;
  projectName: string;
  artifactType: string;
  artifactVersionName: string;

  node: any;
  onSelect: (path: string) => void;
};

const EXT_TO_FILETYPE: Record<string, string> = {
  json: 'JSON Document',
  py: 'Python Source',
  txt: 'Plain Text',
} as const;

const getFileType = (filename: string): string => {
  const ext = filename.split('.').pop() ?? '';
  return EXT_TO_FILETYPE[ext] ?? 'Document';
};

const NodeName = styled.div`
display: inline-flex,
align-items: center;
cursor: pointer;
width: 100%;
`;
NodeName.displayName = 'S.NodeName';

export const ArtifactNodeViewer = ({
  entityName,
  projectName,
  artifactType,
  artifactVersionName,
  node,
  onSelect,
}: ArtifactNodeViewerProps) => {
  if ('artifactId' in node) {
    // render a file
    return (
      <ArtifactFileViewer
        entityName={entityName}
        projectName={projectName}
        artifactType={artifactType}
        artifactVersionName={artifactVersionName}
        path={node.path.join('/')}
      />
    );
  }
  const folderRows = Object.keys(node.folders).map(f => {
    const folder = node.folders[f];
    return {
      searchString: folder.label,
      row: {
        path: '/' + folder.path.join('/'),
        type: 'Folder',
        icon: 'folder-alt',
        label: folder.label,
        sizeBytes: folder.sizeBytes,
      },
    };
  });
  const fileRows = Object.keys(node.files).map(f => {
    const file = node.files[f];
    return {
      searchString: file.label,
      row: {
        path: '/' + file.path.join('/'),
        type: getFileType(file.label),
        icon: 'document',
        label: file.label,
        sizeBytes: file.sizeBytes,
      },
    };
  });
  const rows = [...folderRows, ...fileRows];
  const columns = [
    {
      Header: 'Name',
      accessor: 'label',
      Cell: (cellInfo: CellInfo) => {
        const icon = (
          <Icon name={cellInfo.original.icon} style={{marginRight: '4px'}} />
        );
        return (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              width: '100%',
            }}
            onClick={() => onSelect(cellInfo.original.path)}>
            {icon} {cellInfo.value}
          </div>
        );
      },
    },
    {
      Header: 'Size',
      accessor: 'sizeBytes',
      Cell: (cellInfo: CellInfo) => {
        return numeral(cellInfo.value).format('0.0b');
      },
    },
    {
      Header: 'Type',
      accessor: 'type',
    },
  ];
  return <Table data={rows} columns={columns} />;
};
